/**
 * AssetStatusBadge / AssetConditionBadge
 * Renders a lookup value with its stored hex Color (from the lookup tables),
 * using the themed Badge from the component library (theme-aware).
 */

import { Badge } from '@/components/ui';
import type { BadgeProps } from '@mantine/core';
import { useAssetLookups } from '@/hooks/assetmanagement/useAssetLookups';

type BadgeVariantOption = 'light' | 'filled' | 'outline' | 'dot' | 'default' | 'gradient';
type BaseBadge = Omit<BadgeProps, 'children' | 'color' | 'variant'> & { variant?: BadgeVariantOption };

interface LookupBadgeProps extends BaseBadge {
  name?: string | null;
  color?: string | null;
}

function LookupBadge({ name, color, variant = 'light', size = 'sm', ...props }: LookupBadgeProps) {
  const label = name && name.trim() ? name : 'Unknown';
  return (
    <Badge variant={variant} size={size} color={color || 'gray'} {...props}>
      {label}
    </Badge>
  );
}

interface AssetStatusBadgeProps extends BaseBadge {
  statusId?: number | null;
  statusName?: string | null;
  statusColor?: string | null;
}

/** Status badge. Pass the ModelView's statusName/statusColor, or just a statusId. */
export function AssetStatusBadge({ statusId, statusName, statusColor, ...props }: AssetStatusBadgeProps) {
  const { getStatusById } = useAssetLookups();
  const fromLookup = statusName ? undefined : getStatusById(statusId);
  return (
    <LookupBadge name={statusName ?? fromLookup?.name} color={statusColor ?? fromLookup?.color} {...props} />
  );
}

interface AssetConditionBadgeProps extends BaseBadge {
  conditionId?: number | null;
  conditionName?: string | null;
  conditionColor?: string | null;
}

/** Condition badge. */
export function AssetConditionBadge({ conditionId, conditionName, conditionColor, ...props }: AssetConditionBadgeProps) {
  const { getConditionById } = useAssetLookups();
  if (conditionId == null && !conditionName) return null;
  const fromLookup = conditionName ? undefined : getConditionById(conditionId);
  return (
    <LookupBadge name={conditionName ?? fromLookup?.name} color={conditionColor ?? fromLookup?.color} {...props} />
  );
}
